/**
 * Strategy Export Utility
 *
 * Exports strategy data to CSV or XLSX format for analysis.
 */

import * as XLSX from 'xlsx';
import type {
  CompletedStrategyListItemV2,
  StrategyInfo,
  Order,
  FuturesOrder,
} from '../types/strategy';
import {
  buildPnlSourceView,
  normalizePnlSourceQuality,
  summarizeCompletedPnlByQuality,
} from './pnlSourceQuality';

type ExportFormat = 'csv' | 'xlsx';

/**
 * Format timestamp to locale string
 */
function formatTimestamp(timestamp: number | null | undefined): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Format currency value
 */
function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '0';
  return value.toFixed(2);
}

/**
 * Format percentage value
 */
function formatPercent(value: number | null | undefined): string {
  if (value == null) return '0';
  return value.toFixed(4);
}

/**
 * Calculate duration in human-readable format
 */
function formatDuration(startTime: number, endTime: number): string {
  if (!startTime || !endTime) return '-';
  const durationMs = endTime - startTime;
  if (durationMs < 0) return '-';

  const totalSeconds = Math.floor(durationMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Get final state display name in Korean
 */
function getFinalStateDisplayName(state: string): string {
  switch (state) {
    case 'Success':
    case 'EvaluatePnL':
      return '완료';
    case 'Failure':
      return '실패';
    case 'Abort':
      return '중단';
    default:
      return state;
  }
}

/**
 * Build completed-strategy rows with provenance-safe aggregate totals.
 */
export function buildCompletedStrategiesExportRows(
  strategies: CompletedStrategyListItemV2[],
) {
  const totalInvested = strategies.reduce((sum, s) => sum + (s.pnl_summary.total_spot_cost_usdt || 0), 0);
  const totals = summarizeCompletedPnlByQuality(strategies.map((strategy) => strategy.pnl_summary));
  const verifiedStrategies = strategies.filter(
    (strategy) => normalizePnlSourceQuality(strategy.pnl_summary.source_quality) === 'FetchedVerified',
  );
  const verifiedAvgPnlPercent = verifiedStrategies.length > 0
    ? verifiedStrategies.reduce((sum, s) => sum + (s.pnl_summary.pnl_percent || 0), 0)
      / verifiedStrategies.length
    : null;

  // Header row
  const headers = [
    '전략 ID', '코인', '유형', '최종 상태', '매수 거래소', '매도 거래소',
    '시작일', '종료일', '소요 시간', '투자금 (USDT)', '현물 수익 (USDT)',
    '선물 손익 (USDT)', '펀딩비 (USDT)', '수수료 (USDT)', '순손익 (USDT)', '수익률 (%)',
    '손익 출처 상태', '불완전 사유', '커버 주문', '미완료 주문', '예상 체결 수량', '커버 체결 수량', '커버리지 확인 시각'
  ];

  // Summary row before header
  const summaryRow = [
    `완료된 전략 내보내기 - ${new Date().toLocaleString('ko-KR')}`,
    `검증 완료 손익: ${formatCurrency(totals.verified)}`,
    `동기화 대기 손익: ${formatCurrency(totals.pending)}`,
    `불완전 손익: ${formatCurrency(totals.incomplete)}`,
    `미검증 손익: ${formatCurrency(totals.unverified)}`,
    `총 ${strategies.length}개`,
    '', '', '',
    `총 투자: ${formatCurrency(totalInvested)}`,
    '', '', '', '',
    '',
    `평균 (검증 완료만): ${verifiedAvgPnlPercent == null ? '-' : `${formatPercent(verifiedAvgPnlPercent)}%`}`,
  ];

  // Data rows
  const dataRows = strategies.map((s) => [
    s.strategy_id,
    s.base_currency || '-',
    s.strategy_type,
    getFinalStateDisplayName(s.final_state),
    s.buy_exchange || '-',
    s.sell_exchange || '-',
    formatTimestamp(s.started_at),
    formatTimestamp(s.completed_at),
    formatDuration(s.started_at, s.completed_at),
    formatCurrency(s.pnl_summary.total_spot_cost_usdt),
    formatCurrency(s.pnl_summary.total_spot_revenue_usdt),
    formatCurrency(s.pnl_summary.total_futures_pnl_usdt),
    formatCurrency(s.pnl_summary.total_funding_fees_usdt),
    formatCurrency(s.pnl_summary.total_commission_usdt),
    formatCurrency(s.pnl_summary.total_pnl_usdt),
    formatPercent(s.pnl_summary.pnl_percent),
    normalizePnlSourceQuality(s.pnl_summary.source_quality),
    s.pnl_summary.incomplete_reason || '-',
    (s.pnl_summary.covered_order_count ?? 0).toString(),
    (s.pnl_summary.incomplete_order_count ?? 0).toString(),
    (s.pnl_summary.expected_executed_quantity ?? 0).toString(),
    (s.pnl_summary.covered_executed_quantity ?? 0).toString(),
    formatTimestamp(s.pnl_summary.coverage_checked_at),
  ]);

  return { summaryRow, headers, dataRows };
}

/**
 * Export completed strategies to file
 */
export function exportCompletedStrategies(
  strategies: CompletedStrategyListItemV2[],
  format: ExportFormat = 'xlsx',
  filename?: string
): void {
  const { summaryRow, headers, dataRows } = buildCompletedStrategiesExportRows(strategies);

  // Combine: summary row + header + data
  const allRows = [summaryRow, headers, ...dataRows];

  const ws = XLSX.utils.aoa_to_sheet(allRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '완료된 전략');

  // Set column widths
  ws['!cols'] = [
    { wch: 40 }, // 전략 ID
    { wch: 10 }, // 코인
    { wch: 20 }, // 유형
    { wch: 10 }, // 최종 상태
    { wch: 12 }, // 매수 거래소
    { wch: 12 }, // 매도 거래소
    { wch: 22 }, // 시작일
    { wch: 22 }, // 종료일
    { wch: 12 }, // 소요 시간
    { wch: 15 }, // 투자금
    { wch: 15 }, // 현물 수익
    { wch: 15 }, // 선물 손익
    { wch: 15 }, // 펀딩비
    { wch: 15 }, // 수수료
    { wch: 15 }, // 순손익
    { wch: 12 }, // 수익률
    { wch: 24 }, // 손익 출처 상태
    { wch: 30 }, // 불완전 사유
    { wch: 12 }, // 커버 주문
    { wch: 12 }, // 미완료 주문
    { wch: 16 }, // 예상 체결 수량
    { wch: 16 }, // 커버 체결 수량
    { wch: 22 }, // 커버리지 확인 시각
  ];

  const defaultFilename = `completed_strategies_${new Date().toISOString().split('T')[0]}`;
  const ext = format === 'csv' ? 'csv' : 'xlsx';
  const bookType = format === 'csv' ? 'csv' : 'xlsx';

  XLSX.writeFile(wb, `${filename || defaultFilename}.${ext}`, { bookType });
}

/**
 * Export single strategy detail to file
 */
export function exportStrategyDetail(
  strategy: StrategyInfo,
  format: ExportFormat = 'xlsx',
  filename?: string
): void {
  const wb = XLSX.utils.book_new();
  const ctx = strategy.execution_context;

  // Overview sheet
  const metrics = ctx.metrics || { total_purchased_volume: 0, total_sold_volume: 0 };
  const pnlSource = buildPnlSourceView(
    ctx.pnl_snapshot?.fetched,
    ctx.pnl_snapshot?.calculated_hedge_pnl ?? 0,
  );
  const overviewData = [
    ['전략 개요', ''],
    ['전략 ID', strategy.strategy_id],
    ['전략 유형', strategy.strategy_type],
    ['현재 상태', strategy.current_state],
    ['코인', ctx.opportunity?.base || '-'],
    ['시작일', formatTimestamp(strategy.started_at)],
    ['실행 중', strategy.is_running ? '예' : '아니오'],
    ['모니터링 일시정지', strategy.is_monitoring_paused ? '예' : '아니오'],
    [''],
    ['손익 요약', ''],
    ['투자금 (USDT)', formatCurrency(ctx.pnl_snapshot?.spot_spent_usdt)],
    ['매도 수익 (USDT)', formatCurrency(ctx.pnl_snapshot?.spot_earned_usdt)],
    ['순손익 (USDT)', formatCurrency(ctx.pnl_snapshot?.net_pnl_usdt)],
    ['헤지 손익 (USDT)', formatCurrency(pnlSource.hedgePnl)],
    ['손익 출처 상태', pnlSource.quality],
    ['커버 체결 수량', pnlSource.coveredQuantity.toString()],
    ['예상 체결 수량', pnlSource.expectedQuantity.toString()],
    ['미완료 주문 수', pnlSource.incompleteOrderCount.toString()],
    [''],
    ['수량 정보', ''],
    ['매수 수량', (metrics.total_purchased_volume ?? 0).toString()],
    ['매도 수량', (metrics.total_sold_volume ?? 0).toString()],
    ['잔여 수량', ((metrics.total_purchased_volume ?? 0) - (metrics.total_sold_volume ?? 0)).toString()],
  ];

  const overviewWs = XLSX.utils.aoa_to_sheet(overviewData);
  overviewWs['!cols'] = [{ wch: 25 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, overviewWs, '개요');

  // Spot orders sheet
  const spotOrders = Object.values(ctx.orders || {}) as Order[];
  if (spotOrders.length > 0) {
    const spotData = spotOrders.map((o) => ({
      '시간': formatTimestamp(o.created_at),
      '주문 ID': o.order_id,
      '거래소': o.exchange,
      '페어': o.pair,
      '방향': o.side === 'Bid' ? '매수' : '매도',
      '유형': o.order_type,
      '상태': o.status,
      '주문 수량': o.volume.toString(),
      '체결 수량': o.executed_volume.toString(),
      '주문 가격': o.price.toString(),
      '체결 가격': o.executed_price?.toString() || '-',
      '합계': o.total?.toString() || '-',
      '수수료': o.paid_fee?.toString() || '-',
      '수수료 자산': o.paid_fee_asset || '-',
    }));

    const spotWs = XLSX.utils.json_to_sheet(spotData);
    spotWs['!cols'] = [
      { wch: 22 }, // 시간
      { wch: 20 }, // 주문 ID
      { wch: 10 }, // 거래소
      { wch: 12 }, // 페어
      { wch: 8 },  // 방향
      { wch: 8 },  // 유형
      { wch: 10 }, // 상태
      { wch: 15 }, // 주문 수량
      { wch: 15 }, // 체결 수량
      { wch: 15 }, // 주문 가격
      { wch: 15 }, // 체결 가격
      { wch: 15 }, // 합계
      { wch: 12 }, // 수수료
      { wch: 10 }, // 수수료 자산
    ];
    XLSX.utils.book_append_sheet(wb, spotWs, '현물 주문');
  }

  // Futures orders sheet
  const futuresOrders = Object.values(ctx.futures_orders || {}) as FuturesOrder[];
  if (futuresOrders.length > 0) {
    const futuresData = futuresOrders.map((o) => ({
      '시간': formatTimestamp(o.created_at),
      '주문 ID': o.id,
      '심볼': o.pair,
      '방향': o.side === 'Bid' ? '매수' : '매도',
      '포지션': o.position_side,
      '유형': o.order_type,
      '상태': o.status,
      '주문 수량': o.volume.toString(),
      '체결 수량': o.executed_volume.toString(),
      '주문 가격': o.price.toString(),
      '평균 가격': o.avg_price?.toString() || '-',
      '수수료': o.commission?.toString() || '-',
      '실현 손익': o.realized_pnl?.toString() || '-',
      'Reduce Only': o.reduce_only ? '예' : '아니오',
    }));

    const futuresWs = XLSX.utils.json_to_sheet(futuresData);
    futuresWs['!cols'] = [
      { wch: 22 }, // 시간
      { wch: 20 }, // 주문 ID
      { wch: 12 }, // 심볼
      { wch: 8 },  // 방향
      { wch: 10 }, // 포지션
      { wch: 8 },  // 유형
      { wch: 10 }, // 상태
      { wch: 15 }, // 주문 수량
      { wch: 15 }, // 체결 수량
      { wch: 15 }, // 주문 가격
      { wch: 15 }, // 평균 가격
      { wch: 12 }, // 수수료
      { wch: 12 }, // 실현 손익
      { wch: 10 }, // Reduce Only
    ];
    XLSX.utils.book_append_sheet(wb, futuresWs, '선물 주문');
  }

  // Funding history sheet (if available)
  const incomeHistory = ctx.pnl_snapshot?.fetched?.income_history || [];
  if (incomeHistory.length > 0) {
    const fundingData = incomeHistory.map((i) => ({
      '시간': formatTimestamp(i.time),
      '유형': i.income_type || '-',
      '심볼': i.symbol || '-',
      '수입': i.income?.toString() || '0',
      '자산': i.asset || '-',
      '정보': i.info || '-',
      '거래 ID': i.tran_id?.toString() || '-',
    }));

    const fundingWs = XLSX.utils.json_to_sheet(fundingData);
    fundingWs['!cols'] = [
      { wch: 22 }, // 시간
      { wch: 15 }, // 유형
      { wch: 12 }, // 심볼
      { wch: 15 }, // 수입
      { wch: 8 },  // 자산
      { wch: 30 }, // 정보
      { wch: 15 }, // 거래 ID
    ];
    XLSX.utils.book_append_sheet(wb, fundingWs, '펀딩 내역');
  }

  // User trades sheet (if available)
  const userTrades = ctx.pnl_snapshot?.fetched?.user_trades || [];
  if (userTrades.length > 0) {
    const tradesData = userTrades.map((t) => ({
      '시간': formatTimestamp(t.time),
      '심볼': t.symbol || '-',
      '방향': t.side || '-',
      '포지션': t.positionSide || '-',
      '가격': t.price?.toString() || '0',
      '수량': t.qty?.toString() || '0',
      '거래대금': t.quoteQty?.toString() || '0',
      '수수료': t.commission?.toString() || '0',
      '수수료 자산': t.commissionAsset || '-',
      '실현 손익': t.realizedPnl?.toString() || '0',
      '메이커': t.maker ? '예' : '아니오',
    }));

    const tradesWs = XLSX.utils.json_to_sheet(tradesData);
    tradesWs['!cols'] = [
      { wch: 22 }, // 시간
      { wch: 12 }, // 심볼
      { wch: 8 },  // 방향
      { wch: 10 }, // 포지션
      { wch: 15 }, // 가격
      { wch: 15 }, // 수량
      { wch: 15 }, // 거래대금
      { wch: 12 }, // 수수료
      { wch: 10 }, // 수수료 자산
      { wch: 12 }, // 실현 손익
      { wch: 8 },  // 메이커
    ];
    XLSX.utils.book_append_sheet(wb, tradesWs, '거래 내역');
  }

  const baseCurrency = ctx.opportunity?.base || 'strategy';
  const defaultFilename = `${baseCurrency}_${strategy.strategy_id.slice(0, 8)}_${new Date().toISOString().split('T')[0]}`;
  const ext = format === 'csv' ? 'csv' : 'xlsx';
  const bookType = format === 'csv' ? 'csv' : 'xlsx';

  XLSX.writeFile(wb, `${filename || defaultFilename}.${ext}`, { bookType });
}
